import { Schemas } from '@api-client/generated/types';

type AdminRole = NonNullable<Schemas.User['adminRole']>;

/**
 * Roles allowed on each tab of the company workspace, keyed by the path suffix after
 * `/companies/:companyId` — `''` being the Onboarding index.
 *
 * Single source for both sides of the gate: `getPaths.tsx` builds the route `roles` from it and
 * `LayoutCompany` decides which tabs are clickable. Before that the tab bar was role-blind, so
 * every role saw every tab and the ones the router refused dead-ended in Access denied — the
 * reported SUP-251 symptom [ENG-1197].
 *
 * Lives in constants rather than next to the routes so the layout can read it without importing
 * `@pages` (which imports `@layouts` back, and the cycle leaves one of them undefined at import
 * time).
 *
 * `satisfies` rather than a `Record<string, string[]>` annotation on purpose: it keeps the keys
 * literal (so `CompanyTabPath` below turns a mistyped suffix into a compile error instead of a
 * tab that is silently disabled forever) while still checking every role against `AdminRole`.
 */
export const COMPANY_TAB_ROLES = {
  // Accountants own the Onboarding, Company info and Billing tabs with full edit rights;
  // the matching /admin routes accept the role too [SUP-251].
  '': ['admin', 'kyc', 'accountant'],
  '/info': ['admin', 'kyc', 'accountant'],
  '/documents': ['admin', 'accountant'],
  '/documents/bob50/:id': ['admin', 'accountant', 'kyc'],
  '/documents/:id': ['admin', 'accountant'],
  '/transactions': ['admin', 'accountant'],
  '/transactions/:id': ['admin', 'accountant'],
  '/accounts': ['admin', 'accountant'],
  '/incorporation_old': ['admin', 'kyc'],
  '/incorporation': ['admin', 'kyc'],
  '/legal': ['admin', 'kyc', 'accountant'],
  '/legal/:id': ['admin', 'kyc', 'accountant'],
  '/taxes': ['admin', 'accountant'],
  '/team': ['admin'],
  '/billing': ['admin', 'accountant'],
  '/billing/:billingId': ['admin', 'accountant'],
  '/one-time-billing/:oneTimeBillingId': ['admin', 'accountant'],
  '/processes': ['admin'],
  '/payslips': ['admin', 'accountant'],
  '/payslips/:id': ['admin', 'accountant'],
} satisfies Record<string, AdminRole[]>;

/** The path suffixes that have a company route — every key of `COMPANY_TAB_ROLES`. */
export type CompanyTabPath = keyof typeof COMPANY_TAB_ROLES;

/** Whether `role` may open the company tab at `suffix`. */
export const canOpenCompanyTab = (
  suffix: CompanyTabPath,
  role?: AdminRole
): boolean => {
  // Annotated to widen the literal role types `satisfies` keeps on each entry.
  const allowed: readonly AdminRole[] = COMPANY_TAB_ROLES[suffix];

  return !!role && allowed.includes(role);
};
